<template>
  <v-container class="fill-height" style="background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);">
    <v-row justify="center" align="center" style="width: 100%;">
      <v-col cols="12" sm="10" md="8" lg="6">
        <v-card class="pa-6" elevation="12" style="border-radius: 24px; background: rgba(255, 255, 255, 0.95); backdrop-filter: blur(10px);">
          
          <!-- 오늘의 위로 메시지 -->
          <DailyMessage />

          <v-card-title class="text-center justify-center">
            <h1 class="text-h4 font-weight-bold mb-2 text-primary">
              가석방 계산기
            </h1>
          </v-card-title>
          
          <v-card-subtitle class="text-center mb-5">
            구속일과 형기를 입력하여 가석방 정보를 계산해 보세요.
          </v-card-subtitle>

          <v-card-text>
            <ParoleForm
              v-model:startDate="sentence_start_date"
              v-model:years="sentence_length_years"
              v-model:months="sentence_length_months"
              :errorMessage="error_message"
              @calculate="CalculateParole"
              @hideKeyboard="HideKeyboard"
            />

            <v-divider class="my-4"></v-divider>

            <!-- 디데이 및 프로그레스 바 -->
            <DDayProgress
              :dDayDate="d_day_date"
              :daysRemaining="days_remaining"
              :progressPercentage="progress_percentage"
            />

            <!-- 결과 영역 -->
            <v-slide-y-transition>
              <div v-if="d_day_date">
                <ParoleResults
                  :show="!!d_day_date"
                  :formattedDDayDate="FormatDate(d_day_date)"
                  :formattedOneThirdDate="FormatDate(parole_eligibility_date_one_third)"
                  :formattedTwoThirdsDate="FormatDate(parole_eligibility_date_two_thirds)"
                  v-model:paroleDate="parole_date"
                  :paroleRate="parole_rate"
                  :paroleDateSuffix="parole_date_suffix"
                  @calculateRate="CalculateParoleRate"
                />

                <v-divider class="my-4"></v-divider>

                <!-- 나만의 메모 공간 -->
                <MemoCalendar :dDayDate="d_day_date" />
              </div>
            </v-slide-y-transition>
          </v-card-text>

          <v-card-actions class="justify-center" v-if="d_day_date">
            <v-btn
              icon
              @click="ShareParoleInfo"
              color="primary"
              elevation="2"
              class="mx-2 hover-premium"
            >
              <svg-icon type="mdi" :path="path1"></svg-icon>
            </v-btn>
            <v-btn
              icon
              @click="ResetForm"
              color="secondary"
              elevation="2"
              class="mx-2 hover-premium"
            >
              <svg-icon type="mdi" :path="path2"></svg-icon>
            </v-btn>
            <v-btn
              icon
              href="mailto:idkay0@gmail.com"
              @click="LogEmailClick"
              color="info"
              elevation="2"
              class="mx-2 hover-premium"
            >
              <v-icon>mdi-email</v-icon>
            </v-btn>
          </v-card-actions>
          
        </v-card>
      </v-col>
    </v-row>
    
    <v-btn
      icon
      @click="ScrollToBottom"
      :class="{ 'show-scrollBottom': show_scroll_bottom }"
      class="scroll-to-bottom"
      color="primary"
      elevation="4"
    >
      <v-icon>mdi-arrow-down</v-icon>
    </v-btn>

    <!-- 개인정보 안내 팝업 -->
    <v-dialog v-model="show_privacy_popup" max-width="400" persistent>
      <v-card style="border-radius: 16px;">
        <v-card-title class="text-h6 font-weight-bold text-center pt-4">
          안전한 정보 보관 안내
        </v-card-title>
        <v-card-text class="text-center pa-4">
          본 앱에서 작성하시는 메모나 계산 기록은 **서버에 전송되거나 저장되지 않으며**, 오직 현재 사용 중인 기기에만 안전하게 보관됩니다. 안심하고 사용하셔도 좋습니다.
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn color="primary" variant="elevated" @click="ClosePrivacyPopup" style="border-radius: 8px;">
            확인했습니다
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>


  </v-container>
</template>

<script>
import SvgIcon from '@jamescoyle/vue-icon'
import { mdiExportVariant, mdiRefresh } from '@mdi/js'
import solarlunar from 'solarlunar';
import DailyMessage from './DailyMessage.vue';
import ParoleForm from './ParoleForm.vue';
import DDayProgress from './DDayProgress.vue';
import ParoleResults from './ParoleResults.vue';
import MemoCalendar from './MemoCalendar.vue';
import { analytics } from '../firebase';
import { logEvent } from "firebase/analytics";

export default {
  name: 'ParoleCalculator',
  components: {
    SvgIcon,
    DailyMessage,
    ParoleForm,
    DDayProgress,
    ParoleResults,
    MemoCalendar,
  },
  data() {
    return {
      path1: mdiExportVariant,
      path2: mdiRefresh,
      sentence_start_date: '',
      sentence_length_years: '',
      sentence_length_months: '',
      d_day_date: null,
      parole_date: '',
      parole_date_suffix: '',
      parole_eligibility_date_one_third: null,
      parole_eligibility_date_two_thirds: null,
      parole_rate: null,
      error_message: '',
      year_options: Array.from({ length: 100 }, (_, i) => i.toString()),
      month_options: Array.from({ length: 12 }, (_, i) => i.toString()),
      show_scroll_bottom: false,
      
      // 감성 기능 관련 데이터
      days_remaining: null,
      progress_percentage: 0,
      user_memo: '',
      show_privacy_popup: false,
    }
  },
  mounted() {
    this.LoadParamsFromURL()
    window.addEventListener('scroll', this.HandleScroll)
    
    const popup_seen = localStorage.getItem('parole_calc_popup_seen')
    if (!popup_seen) {
      this.show_privacy_popup = true
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          console.log('요소가 화면에 보입니다.')
        } else {
          console.log('요소가 화면에 보이지 않습니다.')
        }
      })
    })

    observer.observe(document.querySelector('.scroll-to-bottom'))
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.HandleScroll)
  },
  methods: {
    LoadParamsFromURL() {
      const url_params = new URLSearchParams(window.location.search)
      this.sentence_start_date = url_params.get('sentenceStartDate') || ''
      this.sentence_length_years = url_params.get('sentenceLengthYears') || ''
      this.sentence_length_months = url_params.get('sentenceLengthMonths') || ''
      this.parole_date = url_params.get('paroleDate') || ''
      this.CalculateParole()
    },
    CalculateParole() {
      /** Analytics event */
      logEvent(analytics, 'calculate_parole');

      const start_date = new Date(this.sentence_start_date)
      if (isNaN(start_date)) {
        this.error_message =
          '유효하지 않은 날짜입니다. 형 시작일을 다시 입력해 주세요.'
        this.d_day_date = null
        this.parole_eligibility_date_one_third = null
        this.parole_eligibility_date_two_thirds = null
        return
      }
      start_date.setFullYear(
        start_date.getFullYear() + parseInt(this.sentence_length_years || 0),
      )
      start_date.setMonth(
        start_date.getMonth() + parseInt(this.sentence_length_months || 0),
      )
      if (isNaN(start_date)) {
        this.error_message =
          '유효하지 않은 날짜 계산입니다. 형의 길이를 다시 입력해 주세요.'
        this.d_day_date = null
        this.parole_eligibility_date_one_third = null
        this.parole_eligibility_date_two_thirds = null
        return
      }
      this.d_day_date = start_date.toISOString().split('T')[0]
      
      /** 디데이 및 진행률 계산 호출 */
      this.CalculateDDay()

      const parole_start_date = new Date(this.sentence_start_date)
      const total_sentence_time = start_date - parole_start_date

      const parole_eligibility_time_one_third = total_sentence_time * (1 / 3)
      const parole_eligibility_time_two_thirds = total_sentence_time * (2 / 3)

      const parole_date_one_third = new Date(
        parole_start_date.getTime() + parole_eligibility_time_one_third,
      )
      const parole_date_two_thirds = new Date(
        parole_start_date.getTime() + parole_eligibility_time_two_thirds,
      )

      this.parole_eligibility_date_one_third = parole_date_one_third
        .toISOString()
        .split('T')[0]
      this.parole_eligibility_date_two_thirds = parole_date_two_thirds
        .toISOString()
        .split('T')[0]

      /** 예상 가석방 일의 기본 값을 전체 형기 3분의 2 날짜에서 그 달의 말일로 지정 */
      let calculated_parole_date = new Date(this.parole_eligibility_date_two_thirds)
      calculated_parole_date.setMonth(calculated_parole_date.getMonth() + 1)
      calculated_parole_date.setDate(0)

      function LunarToSolar(year, month, day) {
        let result = solarlunar.lunar2solar(year, month, day, false);
        if( result == -1 )
        {
            result = solarlunar.lunar2solar(year, month, day, true);
        }
        return result;
      }

      let buddha = LunarToSolar(calculated_parole_date.getFullYear(), 4, 8)
      const holidays = [
        { month: 2, date: 1 , name:`3.1절 3월1일`},
        { month: buddha.cMonth-1, date: buddha.cDay, name:`석가탄신일 ${buddha.cMonth}월${buddha.cDay}일` },
        { month: 9, date: 28, name:'교정의날 10월28일'},
        { month: 11, date: 25, name:'성탄절 12월25일'},
      ]

      let holiday_check = -1
      holidays.forEach((holiday, index) => {
        if (calculated_parole_date.getMonth() === holiday.month) {
          const holiday_date = new Date(
            calculated_parole_date.getFullYear(),
            holiday.month,
            holiday.date,
          )
          calculated_parole_date = new Date(
            holiday_date.setDate(holiday_date.getDate()),
          )
          holiday_check = index;
          if (calculated_parole_date.getDay() === 0) {
            calculated_parole_date.setDate(
              calculated_parole_date.getDate() - 2,
            )
          } else if (calculated_parole_date.getDay() === 6) {
            calculated_parole_date.setDate(
              calculated_parole_date.getDate() - 1,
            )
          }
        }
      })
      this.parole_date_suffix = '';
      if (holiday_check != -1) {
        this.parole_date_suffix = holidays[holiday_check].name;
        if (calculated_parole_date.getDay() === 0) {
          calculated_parole_date.setDate(
            calculated_parole_date.getDate() - 2,
          )
        } else if (calculated_parole_date.getDay() === 6) {
          calculated_parole_date.setDate(
            calculated_parole_date.getDate() - 1,
          )
        }
      }

      this.parole_date = calculated_parole_date.toISOString().split('T')[0]

      this.error_message = ''
      this.UpdateURL()
      this.CalculateParoleRate()
      this.HandleScroll()
    },
    CalculateParoleRate() {
      const start_date = new Date(this.sentence_start_date)
      const end_date = new Date(this.d_day_date)
      const parole_date = new Date(this.parole_date)

      if (isNaN(parole_date)) {
        this.error_message =
          '입력한 정보가 유효하지 않습니다. 다시 입력해 주세요.'
        this.parole_rate = null
        return
      }

      const total_sentence_time = end_date - start_date
      const parole_time = end_date - parole_date

      this.parole_rate = ((parole_time / total_sentence_time) * 100).toFixed(2)
      this.error_message = ''
      this.UpdateURL()
    },
    /** 디데이 및 진행률 계산 */
    CalculateDDay() {
      if (!this.d_day_date || !this.sentence_start_date) return

      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const end_date = new Date(this.d_day_date)
      const start_date = new Date(this.sentence_start_date)

      const time_diff = end_date - today
      this.days_remaining = Math.ceil(time_diff / (1000 * 60 * 60 * 24))

      const total_time = end_date - start_date
      const served_time = today - start_date
      
      if (total_time > 0) {
        this.progress_percentage = parseFloat(((served_time / total_time) * 100).toFixed(2))
        if (this.progress_percentage > 100) this.progress_percentage = 100
        if (this.progress_percentage < 0) this.progress_percentage = 0
      }
    },
    /** 팝업 닫기 및 상태 저장 */
    ClosePrivacyPopup() {
      /** Analytics event */
      logEvent(analytics, 'click_close_privacy_popup');

      this.show_privacy_popup = false
      localStorage.setItem('parole_calc_popup_seen', 'true')
    },
    LogEmailClick() {
      /** Analytics event */
      logEvent(analytics, 'click_email');
    },
    HideKeyboard() {
      document.activeElement.blur()
    },
    FormatDate(date_string) {
      if (!date_string) return ''
      const date = new Date(date_string)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}년 ${month}월 ${day}일`
    },
    GenerateShareableLink() {
      const params = new URLSearchParams({
        sentenceStartDate: this.sentence_start_date,
        sentenceLengthYears: this.sentence_length_years,
        sentenceLengthMonths: this.sentence_length_months,
        paroleDate: this.parole_date,
      }).toString()
      const url = `${window.location.origin}${window.location.pathname}?${params}`

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(() => {
          alert('링크가 클립보드에 복사되었습니다.')
        }).catch((err) => {
          console.error('클립보드 복사 실패:', err)
          this.FallbackCopyTextToClipboard(url)
        })
      } else {
        this.FallbackCopyTextToClipboard(url)
      }
    },
    FallbackCopyTextToClipboard(text) {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      document.body.appendChild(textarea)
      textarea.select()
      try {
        const successful = document.execCommand('copy')
        const msg = successful ? '링크가 클립보드에 복사되었습니다.' : '복사에 실패했습니다.'
        alert(msg)
      } catch (err) {
        console.error('Fallback 복사 실패:', err)
        alert('클립보드 복사에 실패했습니다. 주소창의 주소를 직접 복사해 주세요.')
      }
      document.body.removeChild(textarea)
    },
    ShareParoleInfo() {
      /** Analytics event */
      logEvent(analytics, 'click_share');

      const release_date = this.FormatDate(this.d_day_date)
      const parole_date = this.FormatDate(this.parole_date)
      const parole_rate = this.parole_rate
      const message = `출소일: ${release_date}\n예상 가석방일: ${parole_date}\n가석방률: ${parole_rate}%\n자세한 정보는 링크를 클릭하세요.`

      if (this.IsMobileDevice()) {
        const params = new URLSearchParams({
          sentenceStartDate: this.sentence_start_date,
          sentenceLengthYears: this.sentence_length_years,
          sentenceLengthMonths: this.sentence_length_months,
          paroleDate: this.parole_date,
        }).toString()
        const url = `${window.location.origin}${window.location.pathname}?${params}`
        if (navigator.share) {
          navigator
            .share({
              title: '가석방 정보',
              text: message,
              url: url,
            })
            .then(() => {
              console.log('가석방 정보가 공유되었습니다.')
            })
            .catch((error) => {
              console.error('공유 실패:', error)
            })
        } else {
          navigator.clipboard.writeText(url).then(() => {
            alert('링크가 클립보드에 복사되었습니다.')
          })
        }
      } else {
        this.GenerateShareableLink()
      }
    },
    IsMobileDevice() {
      return /Mobi|Android/i.test(navigator.userAgent)
    },
    UpdateURL() {
      const params = new URLSearchParams({
        sentenceStartDate: this.sentence_start_date,
        sentenceLengthYears: this.sentence_length_years,
        sentenceLengthMonths: this.sentence_length_months,
        paroleDate: this.parole_date,
        paroleRate: this.parole_rate,
        DDayDate: this.d_day_date,
        paroleEligibilityDateOneThird: this.parole_eligibility_date_one_third,
        paroleEligibilityDateTwoThirds: this.parole_eligibility_date_two_thirds,
      }).toString()
      const new_url = `${window.location.origin}${window.location.pathname}?${params}`
      window.history.replaceState({}, '', new_url)
    },
    ResetForm() {
      /** Analytics event */
      logEvent(analytics, 'click_reset');

      this.sentence_start_date = ''
      this.sentence_length_years = ''
      this.sentence_length_months = ''
      this.parole_date = ''
      this.d_day_date = null
      this.parole_eligibility_date_one_third = null
      this.parole_eligibility_date_two_thirds = null
      this.parole_rate = null
      this.error_message = ''
      this.days_remaining = null
      this.progress_percentage = 0
    },
    ScrollToBottom() {
      /** Analytics event */
      logEvent(analytics, 'click_scroll_to_bottom');

      const footer = document.querySelector('footer')
      const footer_height = footer ? footer.offsetHeight : 0
      window.scrollTo({
        top: document.body.scrollHeight - footer_height,
        behavior: 'smooth',
      })
    },
    HandleScroll() {
      const footer = document.querySelector('footer')
      const footer_height = footer ? footer.offsetHeight : 0
      const scroll_position = window.scrollY + window.innerHeight
      const page_height = document.body.scrollHeight

      this.show_scroll_bottom = scroll_position < page_height - footer_height
    },
    FocusNextElement(ref_name) {
      this.$nextTick(() => {
        this.$refs[ref_name].focus()
      })
    },
  },
  watch: {
    sentence_length_years() {
      this.CalculateParole()
      this.HideKeyboard()
    },
    sentence_length_months() {
      this.CalculateParole()
      this.HideKeyboard()
    },
  },
}
</script>

<style>
.hover-premium {
  transition: transform 0.2s ease, box-shadow 0.2s ease !important;
}
.hover-premium:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
}

.scroll-to-bottom {
  position: fixed;
  bottom: 30px;
  right: 20px;
  z-index: 1000;
  display: none;
}

.show-scrollBottom {
  display: block;
}

.text-error {
  color: #ff5252;
  font-weight: bold;
}

.fluid-date {
  font-size: clamp(0.9rem, 4vw, 1.25rem);
  white-space: nowrap;
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type=number] {
  -moz-appearance: textfield;
}
</style>
